import { Card, Typography } from 'antd';
import { COMPANY } from '@shared/legal';

const { Title, Text, Paragraph } = Typography;

/**
 * Публичная страница тарифов /pricing.
 *
 * Требование ЮKассы для модерации боевого магазина: страница с ценами
 * должна быть доступна без авторизации. Контент перекликается с секцией
 * PricingSection на странице биллинга, но более развёрнутый — с двумя
 * примерами расчётов и блоком про юрлица.
 *
 * Стиль «Графит»: Title level={2} заголовок, level={3} weight 500 цены,
 * без ярких акцентов, фон #F9FAFB, карточки белые.
 */

interface PriceRowProps {
  title: string;
  price: string;
  unit?: string;
  description: string;
  free?: boolean;
}

function PriceRow({ title, price, unit, description, free }: PriceRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 24,
        padding: '20px 0',
        borderBottom: '1px solid #F3F4F6',
      }}
    >
      <div style={{ flex: 1 }}>
        <Text style={{ fontSize: 15, fontWeight: 500, color: '#1A1A1A' }}>
          {title}
        </Text>
        <Paragraph
          type="secondary"
          style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.6 }}
        >
          {description}
        </Paragraph>
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <Title
          level={3}
          style={{
            margin: 0,
            fontWeight: 500,
            color: free ? '#6B7280' : '#1F2937',
          }}
        >
          {price}
        </Title>
        {unit && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {unit}
          </Text>
        )}
      </div>
    </div>
  );
}

export function PricingPage() {
  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>
        Тарифы
      </Title>
      <Paragraph type="secondary" style={{ fontSize: 14, lineHeight: 1.6 }}>
        Сервис RiChat — конструктор ИИ-ассистентов для бизнеса. Оплата
        устроена по принципу «платите за результат»: создать ассистента,
        загрузить базу знаний, протестировать его в кабинете и подключить
        каналы можно бесплатно — деньги нужны только когда ассистент
        начинает работать с реальными клиентами.
      </Paragraph>

      <Card styles={{ body: { padding: '8px 24px 24px' } }}>
        <PriceRow
          title="Диалоги в боевых каналах"
          price="10 ₽"
          unit="за пару"
          description="Каждая пара «вопрос клиента + ответ ассистента» в Telegram, MAX или виджете на сайте стоит 10 ₽ (5 ₽ вопрос + 5 ₽ ответ). Цена фиксированная и не зависит от длины сообщений или наличия картинок в ответе."
        />
        <PriceRow
          title="Обработка базы знаний"
          price="0,5 ₽"
          unit="за страницу"
          description="При загрузке документов списывается 0,5 ₽ за страницу. Загрузка не блокируется при нехватке средств — стоимость спишется при следующем пополнении, поэтому настроить ассистента можно сразу, не оплачивая аванс."
        />
        <PriceRow
          title="Картинки в документах"
          price="1 ₽"
          unit="за картинку"
          description="Картинки из документов распознаются и попадают в базу знаний, чтобы ассистент мог на них ссылаться. Тарифицируются отдельно от страниц."
        />
        <PriceRow
          title="Тест-чат в кабинете"
          price="Бесплатно"
          free
          description="Общение с ассистентом в тест-чате не тарифицируется. Проверяйте качество ответов и правьте настройки сколько угодно — баланс не расходуется."
        />
      </Card>

      <Title level={4} style={{ marginTop: 32, marginBottom: 12 }}>
        Пополнение баланса
      </Title>
      <Card>
        <Paragraph style={{ margin: 0, fontSize: 14, lineHeight: 1.7 }}>
          Баланс пополняется через ЮKассу на конкретного ассистента.
          Минимальная сумма пополнения — <Text strong style={{ fontWeight: 500 }}>1 000 ₽</Text>, выше — любая.
          Пополнение зачисляется на баланс мгновенно после подтверждения оплаты
          (в редких случаях — до 12 часов). Для юридических лиц и индивидуальных
          предпринимателей доступна оплата по счёту.
        </Paragraph>
        <Paragraph style={{ margin: '12px 0 0', fontSize: 14, lineHeight: 1.7 }}>
          При создании первого ассистента на его баланс начисляется бонус
          <Text strong style={{ fontWeight: 500 }}> 1 000 ₽</Text> — этого хватает
          примерно на 100 диалогов с клиентами, чтобы попробовать сервис в деле
          без пополнения.
        </Paragraph>
      </Card>

      <Title level={4} style={{ marginTop: 32, marginBottom: 12 }}>
        Примеры расчёта
      </Title>
      <Card>
        <Paragraph style={{ marginBottom: 12, fontSize: 14, lineHeight: 1.7 }}>
          <Text strong style={{ fontWeight: 500 }}>Малый магазин:</Text> в день
          ассистенту приходит 10 вопросов от клиентов через виджет на сайте.
          За месяц это ≈ 300 пар вопрос-ответ. Стоимость: <Text strong style={{ fontWeight: 500 }}>3 000 ₽/месяц</Text>.
        </Paragraph>
        <Paragraph style={{ margin: 0, fontSize: 14, lineHeight: 1.7 }}>
          <Text strong style={{ fontWeight: 500 }}>Загрузка базы знаний:</Text>
          {' '}один документ на 50 страниц с 20 картинками — 50 × 0,5 + 20 × 1 =
          <Text strong style={{ fontWeight: 500 }}> 45 ₽</Text> единовременно.
          База знаний грузится один раз, дальше не списывается.
        </Paragraph>
      </Card>

      <Title level={4} style={{ marginTop: 32, marginBottom: 12 }}>
        Для юридических лиц
      </Title>
      <Card>
        <Paragraph style={{ margin: 0, fontSize: 14, lineHeight: 1.7 }}>
          Юридические лица и индивидуальные предприниматели могут оплачивать
          сервис на основании счёта. После оплаты формируется универсальный
          передаточный документ (УПД), который размещается в кабинете.
          Лицензионное вознаграждение по сервису не облагается НДС
          (пп. 26 п. 2 ст. 149 НК РФ). Реквизиты для оплаты — в разделе
          «Реквизиты».
        </Paragraph>
      </Card>

      <Paragraph
        type="secondary"
        style={{ marginTop: 24, fontSize: 12, textAlign: 'center' }}
      >
        Полные условия использования — в Пользовательском соглашении.
        Услуги предоставляет {COMPANY.shortName}, ИНН {COMPANY.inn}.
      </Paragraph>
    </div>
  );
}
